import { CommonModule } from '@angular/common';
import { Component, Input, forwardRef, ViewEncapsulation } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'app-custom-select',
    templateUrl: './custom-select.component.html',
    styleUrls: ['./custom-select.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        MatIconModule,
        MatSelectModule
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CustomSelectComponent),
            multi: true
        }
    ],
    // ViewEncapsulation.None is often needed for mat-select panel styling 
    // if the panel class is defined in this component's SCSS
    encapsulation: ViewEncapsulation.None
})
export class CustomSelectComponent implements ControlValueAccessor {
    @Input() label: string = '';
    @Input() items: any[] = [];
    @Input() placeholder: string = '';
    @Input() bindLabel: string = 'label';
    @Input() bindValue: string = 'value';
    @Input() optionIconKey?: string;
    private _disabled = false;
    @Input()
    set disabled(value: boolean) {
        this._disabled = !!value;
        this.isDisabled = this._disabled;
    }
    get disabled(): boolean {
        return this._disabled;
    }

    value: any = null;
    isDisabled: boolean = false;

    onTouchedFlag = false;

    onChange: any = () => { };
    onTouched: any = () => { };

    writeValue(value: any): void {
        this.value = value;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    onSelectionChange(event: any): void {
        this.value = event.value;
        this.onChange(this.value);
        this.onTouched();
        this.onTouchedFlag = true;
    }

    onOpenedChange(opened: boolean): void {
        if (!opened) {
            this.onTouched();
            this.onTouchedFlag = true;
        }
    }

    getDisplayValue(item: any): string {
        if (item === null || item === undefined) return '';
        if (typeof item !== 'object') {
            return String(item);
        }
        return item[this.bindLabel];
    }

    getDisabledReason(item: any): string {
        if (!item || typeof item !== 'object') {
            return '';
        }
        return String(item.disabledReason || '');
    }

    isOptionDisabled(item: any): boolean {
        return !!(item && typeof item === 'object' && item.disabled);
    }

    getBindingValue(item: any): any {
        if (typeof item === 'object' && this.bindValue) {
            // Check if the object actually has the property, otherwise return item
            if (this.bindValue in item) {
                return item[this.bindValue];
            }
            return item;
        }
        return item;
    }

    getSelectedLabel(): string {
        if (this.value === null || this.value === undefined) return '';

        // Find the item that corresponds to the current value
        const selectedItem = this.items.find(item => {
            return this.getBindingValue(item) === this.value;
        });

        if (selectedItem) {
            return this.getDisplayValue(selectedItem);
        }

        // Fallback if not found (maybe value matches display if simple string)
        return String(this.value);
    }

    getSelectedItem(): any {
        if (this.value === null || this.value === undefined) return undefined;
        return this.items.find(item => this.getBindingValue(item) === this.value);
    }

    trackByOption(index: number, item: any): string {
        const bound = this.getBindingValue(item);
        if (bound !== null && bound !== undefined && typeof bound !== 'object') {
            return `v:${String(bound)}`;
        }

        const label = this.getDisplayValue(item).trim();
        if (label) {
            return `l:${label}:${index}`;
        }

        return `idx:${index}`;
    }

    getOptionIcon(item: any): string | null {
        if (!this.optionIconKey || typeof item !== 'object') {
            return null;
        }
        return item[this.optionIconKey] || null;
    }

    hasOptionIcon(item: any): boolean {
        return !!this.getOptionIcon(item);
    }

    getSelectedIcon(): string | null {
        const selectedItem = this.getSelectedItem();
        if (!selectedItem) return null;
        return this.getOptionIcon(selectedItem);
    }
}
